import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]

const toDateKey = (d) => {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return ""
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Monday as first column */
const mondayOffset = (d) => (d.getDay() + 6) % 7

const buildMonthGrid = (visibleMonth) => {
  const year = visibleMonth.getFullYear()
  const month = visibleMonth.getMonth()
  const first = new Date(year, month, 1)
  const pad = mondayOffset(first)
  const start = new Date(year, month, 1 - pad)
  const days = []
  for (let i = 0; i < 42; i++) {
    const cell = new Date(start)
    cell.setDate(start.getDate() + i)
    days.push(cell)
  }
  return days
}

function Calendar({
  className,
  mode = "single",
  selected,
  modifiers,
  modifiersStyles,
  showOutsideDays = true,
}) {
  const today = new Date()
  const [visibleMonth, setVisibleMonth] = React.useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1)
  )

  const selectedList = React.useMemo(() => {
    if (mode === "multiple" && Array.isArray(selected)) return selected
    if (selected instanceof Date) return [selected]
    if (selected) return [selected]
    return []
  }, [mode, selected])

  const selectedKeys = React.useMemo(() => {
    return new Set(selectedList.map(toDateKey).filter(Boolean))
  }, [selectedList])

  const modifierKeySets = React.useMemo(() => {
    if (!modifiers) return {}
    return Object.fromEntries(
      Object.entries(modifiers).map(([name, dates]) => {
        const list = Array.isArray(dates) ? dates : dates ? [dates] : []
        return [name, new Set(list.map(toDateKey).filter(Boolean))]
      })
    )
  }, [modifiers])

  const cells = React.useMemo(
    () => buildMonthGrid(visibleMonth),
    [visibleMonth]
  )

  const monthLabel = visibleMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  })

  const handlePrevMonth = () => {
    setVisibleMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setVisibleMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  }

  return (
    <div className={cn("p-3", className)}>
      <div className="relative flex items-center justify-center pt-1">
        <button
          type="button"
          aria-label="Previous month"
          className={cn(
            buttonVariants({ variant: "outline" }),
            "absolute left-1 h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100"
          )}
          onClick={handlePrevMonth}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium">{monthLabel}</span>
        <button
          type="button"
          aria-label="Next month"
          className={cn(
            buttonVariants({ variant: "outline" }),
            "absolute right-1 h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100"
          )}
          onClick={handleNextMonth}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 w-full" role="grid" aria-label="Calendar">
        <div className="flex w-full">
          {WEEKDAYS.map((wd) => (
            <div
              key={wd}
              className="text-muted-foreground flex-1 text-center text-[0.8rem] font-normal"
              role="columnheader"
            >
              {wd}
            </div>
          ))}
        </div>
        <div className="mt-2 flex w-full flex-col gap-1">
          {Array.from({ length: 6 }, (_, row) => (
            <div key={row} className="flex w-full gap-0" role="row">
              {cells.slice(row * 7, row * 7 + 7).map((day) => {
                const key = toDateKey(day)
                const inMonth =
                  day.getMonth() === visibleMonth.getMonth() &&
                  day.getFullYear() === visibleMonth.getFullYear()
                if (!inMonth && !showOutsideDays) {
                  return (
                    <div
                      key={key + row}
                      className="flex h-8 flex-1 items-center justify-center p-0"
                      aria-hidden
                    />
                  )
                }

                const isToday =
                  toDateKey(today) === key && inMonth
                const isSelected = selectedKeys.has(key)
                const activeModifiers = Object.entries(modifierKeySets)
                  .filter(([, set]) => set.has(key))
                  .map(([name]) => name)

                const modifierStyle = {}
                for (const name of activeModifiers) {
                  Object.assign(modifierStyle, modifiersStyles?.[name])
                }

                const isOutside = !inMonth

                return (
                  <div
                    key={key + row}
                    className="relative flex flex-1 justify-center p-0 text-center text-sm"
                    role="gridcell"
                  >
                    <span
                      className={cn(
                        buttonVariants({ variant: "ghost" }),
                        "h-8 w-8 p-0 font-normal",
                        isOutside &&
                          "text-muted-foreground opacity-60 aria-selected:text-muted-foreground",
                        isToday &&
                          inMonth &&
                          !isSelected &&
                          !activeModifiers.length &&
                          "bg-accent text-accent-foreground",
                        isSelected &&
                          !activeModifiers.length &&
                          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                      )}
                      style={Object.keys(modifierStyle).length ? modifierStyle : undefined}
                      aria-selected={isSelected || activeModifiers.length > 0}
                    >
                      {day.getDate()}
                    </span>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

Calendar.displayName = "Calendar"

export { Calendar }
